import { VIBE_BUILD_SLO_POLICY } from "../../../vibe/vibe-build-health-policy";
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

const { queue, significance } = VIBE_BUILD_SLO_POLICY;

/**
 * A tenant's build jobs are waiting on the platform longer than the queue-time
 * objective allows — `SweepVibeBuildHealthUseCase`, over `VIBE_BUILD_SLO_POLICY`.
 *
 * Raised on either of two findings, both on the payload:
 *
 * - a window's evidence refutes the objective (the binomial test described on
 *   the policy), so the tenant's p95 wait is over the objective with confidence
 *   rather than by one unlucky build job;
 * - a build job is still waiting, with a slot of its org's cap free, past the
 *   starvation bound — a queue that stopped moving, which no ratio can see.
 *
 * Only waits the PLATFORM caused count: time a build job spent behind its own
 * org's concurrency cap is the cap working and is excluded, so a busy tenant
 * cannot trip this on its own traffic.
 *
 * Retracted on the first sweep whose windows no longer refute the objective and
 * whose queue holds no starved build job.
 */
export const VIBE_BUILD_QUEUE_SLO_BREACHED = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message: `This tenant's Vibe build jobs are waiting on the platform longer than the queue-time objective: p95 under ${queue.objectiveSeconds} s, counting only the time a build job waited while a slot of its org's own concurrency cap was free. Either a window's evidence refutes the objective at p <= ${significance}, or a build job has been waiting with a slot free for more than ${queue.starvationSeconds} s. The payload names the window, the counts, the observed p95 and any starved build job. Customers see their pushes sit in "queued" while nothing of theirs is building.`,
  severity: "high",
  remedy:
    "Read the payload: `starved` lists build jobs waiting right now, `windows` shows which window breached and how many build jobs waited too long. A starved build job with a free slot means the claimer is not claiming — check the `VIBE_BUILD_RUNNER_TICK` cron's executions on the admin Cron page for failures or a stalled lock, then the backend logs for `ClaimVibeBuildJobUseCase`. A breach with nothing starved is slow admission: check whether the tenant's executor is refusing or slow to acknowledge (the build jobs' STARTING stamps against their admission), and whether the Nomad client pool has room (the tenant's node-health alarms). This clears on its own once the windows recover and nothing is starved."
} as const satisfies IssueDefinition;
